import Paragraph from 'components/TextComps/Paragraph';
import './FAQ.scss';
import { Subtitle } from 'components/TextComps/Subtitle';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DefaultRoutes } from 'Routes';
import { Accordion } from '@trackman/web-shared-components';
import { useState } from 'react';
import { subscription } from 'pages/Webshop/Shop/Shop';

export const FAQ = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const qna = [
    {
      q: t('WhatIsTheFirstStepToBecomingTrackmanCertified', { ns: 'certificationsFAQ' }),
      a: (
        <Trans
          i18nKey='AllTrackmanCertificationsStartWith'
          ns='certificationsFAQ'
          defaults='All Trackman certifications start with <bold>Ball Flight Level 1</bold>, which is the mandatory foundation course. This certification teaches the core principles of ball flight and Trackman data interpretation that underpin all advanced certifications.'
          components={{ bold: <strong /> }}
        />
      ),
    },
    {
      q: t('HowDoITakeATrackmanCertificationCourse', { ns: 'certificationsFAQ' }),
      a: <>{t('AllCertificationsAreCompletedOnline', { ns: 'certificationsFAQ' })}</>,
    },
    {
      q: t('HowMuchDoesTrackmanCertificationCost', { ns: 'certificationsFAQ' }),
      a: (
        <div className='multiline-answer'>
          <div>{t('TrackmanCertificationsArePurchasedIndividually', { ns: 'certificationsFAQ' })}</div>
          <div>{t('TrackmanPremiumMembersReceiveADiscount', { ns: 'certificationsFAQ' })}</div>
        </div>
      ),
    },
    {
      q: t('WhatIsTrackmanPremiumMembership', { ns: 'certificationsFAQ' }),
      a: (
        <div className='multiline-answer'>
          <div>{t('TrackmanPremiumIsAnOptionalMembership', { ns: 'certificationsFAQ' })}</div>
          <div>
            <Trans
              i18nKey='PremiumMembersReceive'
              ns='certificationsFAQ'
              defaults='For <bold>${{price}} per year</bold>, Premium members receive:'
              values={{ price: subscription.price }}
              components={{ bold: <strong /> }}
            />
          </div>
          <ul>
            <li>{t('50%DiscountOnTrackmanCertifications', { ns: 'certificationsFAQ' })}</li>
            <li>{t('EarlyAccessToInsightsAndDiscoveries', { ns: 'certificationsFAQ' })}</li>
            <li>{t('InvitationsToWorkshopsAndEvents', { ns: 'certificationsFAQ' })}</li>
            <li>{t('VisibilityThroughTrackmanListings', { ns: 'certificationsFAQ' })}</li>
          </ul>
          <div>{t('MembershipIsFlexible', { ns: 'certificationsFAQ' })}</div>
        </div>
      ),
    },
    {
      q: t('HowLongIsTrackmanCertificationValid', { ns: 'certificationsFAQ' }),
      a: <>{t('TrackmanCertificationsAreValid', { ns: 'certificationsFAQ' })}</>,
    },
    {
      q: t('HowDoITakeTheLevel1OrLevel2CertificationTest', { ns: 'certificationsFAQ' }),
      a: (
        <Trans
          i18nKey='CertificationTestsAreAvailableWithinModules'
          ns='certificationsFAQ'
          defaults='Certification tests are available directly within the different modules in <bold>the Learning Hub</bold>. Once you have purchased access to certifications, you can access the certification test at the bottom of the table of contents in the module.'
          components={{ bold: <strong /> }}
        />
      ),
    },
    {
      q: t('HowDoIBecomeAMasterCertifiedTrackmanProfessional', { ns: 'certificationsFAQ' }),
      a: (
        <div className='multiline-answer'>
          <div>{t('MasterCertificationRepresentsTheHighestLevelOfTrackmanExpertise', { ns: 'certificationsFAQ' })}</div>
          <div>
            <Trans i18nKey='ToApplyPleaseVisit' ns='certificationsFAQ'>
              To apply, please visit&nbsp;
              <span onClick={() => navigate(`${DefaultRoutes.Certifications}/ApplyForMaster`)} className='link'>
                https://trackmanuniversity.com/Certifications/ApplyForMaster
              </span>
              &nbsp; or contact Trackman University at: <a href='mailto:master@trackmanuniversity.com'>master@trackmanuniversity.com</a>
            </Trans>
          </div>
        </div>
      ),
    },
    {
      q: t('CanPGMOrPGAStudentsBecomeTrackmanCertified', { ns: 'certificationsFAQ' }),
      a: (
        <div className='multiline-answer'>
          <div>
            <Trans
              i18nKey='TrackmanUniversityOffersCertification'
              ns='certificationsFAQ'
              defaults='Yes. Trackman University offers certification opportunities for <bold>PGM and PGA students</bold>.'
              components={{ bold: <strong /> }}
            />
          </div>
          <div>
            <Trans i18nKey='PleaseContactYourSalesRepresentative' ns='certificationsFAQ'>
              Please contact your Sales representative or <a href='mailto:events@trackmanuniversity.com'>events@trackmanuniversity.com</a>{' '}
              to&nbsp; learn more about available student programs.
            </Trans>
          </div>
        </div>
      ),
    },
    {
      q: t('DoYouOfferProgramsForCollegesOrGolfAcademies', { ns: 'certificationsFAQ' }),
      a: (
        <div className='multiline-answer'>
          <div>{t('TrackmanUniversityCanIntegrateItsCurriculum', { ns: 'certificationsFAQ' })}</div>
          <div>
            <Trans i18nKey='ForMoreInformationPleaseContact' ns='certificationsFAQ'>
              For more information, please contact <a href='mailto:events@trackmanuniversity.com'>events@trackmanuniversity.com</a>.{' '}
            </Trans>
          </div>
        </div>
      ),
    },
  ];

  const [expandedPanelIndex, setExpandedPanelIndex] = useState<number | null>(null);
  const handlePanelExpandedState = (index: number) => {
    if (expandedPanelIndex === index) {
      setExpandedPanelIndex(null);
    } else {
      setExpandedPanelIndex(index);
    }
  };

  return (
    <div className='certificates-faq'>
      <Subtitle>
        <>{t('FrequentlyAskedQuestions', { ns: 'certificationsFAQ' })}</>
      </Subtitle>
      <Paragraph>
        <>{t('FindTheAnswers', { ns: 'certificationsFAQ' })}</>
      </Paragraph>
      <div className='certificates-faq-entries'>
        {qna.map((item, index) => (
          <Accordion
            key={`faq-accordion-${index}`}
            expanded={expandedPanelIndex === index}
            onChange={() => handlePanelExpandedState(index)}
            severity='primary'
            title={item.q}
          >
            <Paragraph>
              <>{item.a}</>
            </Paragraph>
          </Accordion>
        ))}
      </div>
    </div>
  );
};
